"use client";

import { useId, useRef, useState, type FormEvent } from "react";
import Link from "next/link";
import { publicStorePath } from "@/lib/storefront/public-url";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";

type SignupError = "invalidEmail" | "consentRequired" | "rateLimited" | "error" | "unavailable";
export type NewsletterSignupCopy = (typeof import("@/messages/de.json"))["newsletterSignup"];

export function NewsletterSignup({
	locale,
	copy,
	source = "newsletter-signup",
}: {
	locale: string;
	copy: NewsletterSignupCopy;
	source?: "newsletter-signup" | "black-friday-early-access";
}) {
	const t = (key: keyof NewsletterSignupCopy) => copy[key];
	const id = useId();
	const emailInput = useRef<HTMLInputElement>(null);
	const consentInput = useRef<HTMLInputElement>(null);
	const inFlight = useRef(false);
	const [pending, setPending] = useState(false);
	const [error, setError] = useState<SignupError | null>(null);
	const [success, setSuccess] = useState<"success" | "alreadySubscribed" | null>(null);

	async function subscribe(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (inFlight.current) return;
		setError(null);
		const email = emailInput.current?.value.trim() ?? "";
		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.length > 254) {
			setError("invalidEmail");
			emailInput.current?.focus();
			return;
		}
		if (!consentInput.current?.checked) {
			setError("consentRequired");
			consentInput.current?.focus();
			return;
		}
		inFlight.current = true;
		setPending(true);
		try {
			const response = await fetch("/api/newsletter/subscribe", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({
					email,
					locale,
					source,
					surface: source === "black-friday-early-access" ? "black-friday" : "footer",
					consent: true,
				}),
			});
			const result: unknown = await response.json();
			const data = result as {
				success?: unknown;
				pendingConfirmation?: unknown;
				alreadySubscribed?: unknown;
				skipped?: unknown;
				error?: unknown;
			} | null;
			if (response.ok && data?.success === true && data.alreadySubscribed === true) {
				setSuccess("alreadySubscribed");
			} else if (response.ok && data?.success === true && data.pendingConfirmation === false) {
				setSuccess("success");
			} else if (response.ok && data?.success === false && data.skipped === "unavailable") {
				setError("unavailable");
			} else {
				setError(
					response.status === 429
						? "rateLimited"
						: data?.error === "invalid_email"
							? "invalidEmail"
							: data?.error === "consent_required"
								? "consentRequired"
								: "error",
				);
			}
		} catch {
			setError("error");
		} finally {
			inFlight.current = false;
			setPending(false);
		}
	}

	return (
		<section aria-labelledby={`${id}-title`} className="grid gap-6 py-8 sm:py-10 md:grid-cols-2 md:gap-12">
			<div className="max-w-md">
				<h2 id={`${id}-title`} className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
					{t("title")}
				</h2>
				<p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("description")}</p>
			</div>
			{success ? (
				<p
					role="status"
					className="self-center rounded-md border border-border bg-background p-4 text-sm leading-relaxed text-foreground"
				>
					{t(success)}
				</p>
			) : (
				<form
					aria-labelledby={`${id}-title`}
					aria-busy={pending}
					onSubmit={subscribe}
					noValidate
					className="min-w-0 space-y-4"
				>
					<div>
						<label htmlFor={`${id}-email`} className="mb-2 block text-sm font-medium">
							{t("emailLabel")}
						</label>
						<div className="flex flex-col gap-3 sm:flex-row">
							<Input
								ref={emailInput}
								id={`${id}-email`}
								name="email"
								type="email"
								autoComplete="email"
								autoCapitalize="none"
								spellCheck={false}
								required
								maxLength={254}
								disabled={pending}
								placeholder={t("emailPlaceholder")}
								aria-invalid={error === "invalidEmail"}
								aria-describedby={error === "invalidEmail" ? `${id}-error` : undefined}
								className="h-11 min-w-0"
							/>
							<Button
								type="submit"
								disabled={pending}
								className={
									source === "black-friday-early-access"
										? "h-auto min-h-11 min-w-0 shrink-0 whitespace-normal py-3 sm:max-w-[55%]"
										: "h-11 shrink-0"
								}
							>
								{t(pending ? "submitting" : "submit")}
							</Button>
						</div>
					</div>
					<label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-foreground">
						<input
							ref={consentInput}
							type="checkbox"
							name="consent"
							required
							disabled={pending}
							aria-invalid={error === "consentRequired"}
							aria-describedby={error === "consentRequired" ? `${id}-error` : undefined}
							className="mt-1 h-4 w-4 shrink-0 rounded border-input text-primary focus:ring-ring"
						/>
						<span>{t("consentExplanation")}</span>
					</label>
					<Link
						href={publicStorePath(locale, "/privacy")}
						className="inline-block text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
					>
						{t("privacyLink")}
					</Link>
					{error && (
						<p id={`${id}-error`} role="alert" className="text-sm text-destructive">
							{t(error)}
						</p>
					)}
				</form>
			)}
		</section>
	);
}
